import Phaser from 'phaser';
import { Cell } from '../objects/Cell';
import { NutrientSystem } from '../systems/NutrientSystem';
import { EnvironmentSystem } from '../systems/EnvironmentSystem';
import { BubbleSystem } from '../systems/BubbleSystem';
import { ProtoCellUISystem } from '../systems/ProtoCellUISystem';
import { ProtoCell } from '../objects/ProtoCell';
import { Lipid } from '../objects/Lipid';
import { Molecule, MoleculeType } from '../objects/Molecule';
import { Tooltip } from '../objects/Tooltip';
import { Membrane } from '../objects/Membrane';
import { GameSettings } from '../utils/GameSettings';
import { GameUISystem } from '../systems/GameUISystem';

// Scene 1: Early Protocell (Assembly Phase)
export class ProtocellScene extends Phaser.Scene {
  private cell!: Cell;
  private nutrientSystem!: NutrientSystem;
  private environmentSystem!: EnvironmentSystem;
  private bubbleSystem!: BubbleSystem;
  private protoCell!: ProtoCell;
  private uiSystem!: ProtoCellUISystem;
  private tooltip!: Tooltip;
  private backButton!: Phaser.GameObjects.Text;
  private gradientBackground!: Phaser.GameObjects.Sprite;
  
  // UI elements (not used in new implementation - kept for compatibility)
  private uiContainer!: Phaser.GameObjects.Container;
  private energyText!: Phaser.GameObjects.Text;
  private stabilityText!: Phaser.GameObjects.Text;
  
  // Camera references
  private mainCam!: Phaser.Cameras.Scene2D.Camera;
  private uiCam!: Phaser.Cameras.Scene2D.Camera;
  
  // Zoom constraints
  private readonly MIN_ZOOM: number = 0.8;
  private readonly MAX_ZOOM: number = 2.0;
  private readonly ZOOM_FACTOR: number = 0.1;
  
  // Protocell assembly components
  private components: (Lipid | Molecule)[] = [];
  private autoAssembleMode: boolean = false;
  
  // Lipid and molecule colors
  private lipidColors: number[] = [0xff5555, 0x55ff55, 0x5555ff, 0xffff55, 0xff55ff];
  private moleculeColors: number[] = [0xff9900, 0x00ffff, 0x9900ff, 0xffff00];
  
  // Evolution progress tracking
  private membraneStability: number = 0;
  private absorbedLipids: number = 0;
  private absorbedProteins: number = 0;
  
  // Add property for living membrane
  private livingMembrane: Membrane | null = null;
  
  // Debug UI elements
  private debugContainer: Phaser.GameObjects.Container | null = null;
  private debugBackground: Phaser.GameObjects.Rectangle | null = null;
  private debugTitle: Phaser.GameObjects.Text | null = null;
  private debugStats: Phaser.GameObjects.Text | null = null;
  private isDebugVisible: boolean = false;
  
  // FPS display
  private fpsText: Phaser.GameObjects.Text | null = null;
  private settingsManager: GameSettings | null = null;
  
  // UI references
  private gameUI!: GameUISystem; // Add direct reference to GameUISystem
  
  constructor() {
    super('ProtocellScene');
  }

  init(data: any): void {
    console.log('ProtocellScene init method called');
    
    // Initialize with data from previous scene if available
    if (data) {
      this.membraneStability = data.membraneStability || 0;
      this.absorbedLipids = data.absorbedLipids || 0;
      this.absorbedProteins = data.absorbedProteins || 0;
    }
    
    // Check for and remove any leftover gradient textures
    if (this.textures.exists('protocellGradientTexture')) {
      console.log('Found leftover gradient texture in ProtocellScene init, removing it');
      this.textures.remove('protocellGradientTexture');
    }
  }

  create(): void {
    console.log('ProtocellScene create method called');
    
    // Apply saved FPS settings
    this.settingsManager = GameSettings.getInstance();
    this.settingsManager.applyFpsSetting(this.game);
    
    // Set a solid dark background color for the entire world
    this.cameras.main.setBackgroundColor('#000022'); // Deeper blue for primordial ocean
    
    // Create FPS display if enabled in settings
    this.createFpsDisplay();
    
    // Configure main camera
    this.mainCam = this.cameras.main;
    this.mainCam.setName('mainCam');
    this.mainCam.setZoom(1);
    
    // Create a separate UI camera that doesn't move or zoom
    this.uiCam = this.cameras.add(0, 0, this.scale.width, this.scale.height);
    this.uiCam.setName('uiCam');
    this.uiCam.setScroll(0, 0);
    this.uiCam.transparent = true;
    
    // Create UI container for all UI elements
    this.uiContainer = this.add.container(0, 0);
    this.uiContainer.setDepth(1000);
    
    // Initialize direct access to GameUISystem
    this.gameUI = new GameUISystem(this, {
      progressBarColor: 0xff9900, // Orange for stability
      buttonBgColor: 0x4CAF50
    });
    
    // Create tooltip for component information
    this.tooltip = new Tooltip(this);
    // Add tooltip to the scene
    this.add.existing(this.tooltip);
    // Add tooltip to UI container
    this.uiContainer.add(this.tooltip);
    
    // Initialize systems
    this.nutrientSystem = new NutrientSystem(this);
    this.environmentSystem = new EnvironmentSystem(this);
    this.bubbleSystem = new BubbleSystem(this, 30);
    
    // Create gradient background (very important: do this BEFORE creating interactive elements)
    this.createGradientBackground();
    
    // Create protocell in center of screen
    this.protoCell = new ProtoCell(this, this.scale.width / 2, this.scale.height / 2);
    
    // Listen for completion event from protocell
    this.protoCell.completeEmitter.on('complete', this.handleProtocellCompletion, this);
    
    // Initialize UI AFTER protoCell is created
    this.uiSystem = new ProtoCellUISystem(this, this.protoCell);
    
    // Start bubble system
    this.bubbleSystem.start();
    
    // Set up standard Phaser input system with explicit settings
    this.input.enabled = true;
    this.input.topOnly = true; // Only detect the top-most game object
    this.input.dragDistanceThreshold = 0;
    
    // Create initial components
    this.createInitialComponents();
    
    // Set up drag events after components are created
    this.setupDragEvents();
    
    // Set up mouse wheel zoom
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, gameObjects: any, deltaX: number, deltaY: number, deltaZ: number) => {
      // Determine zoom direction based on wheel movement
      const zoomDirection = deltaY > 0 ? -1 : 1;
      
      // Calculate new zoom level
      const newZoom = Phaser.Math.Clamp(
        this.mainCam.zoom + (zoomDirection * this.ZOOM_FACTOR),
        this.MIN_ZOOM,
        this.MAX_ZOOM
      );
      
      // Only proceed if zoom actually changes
      if (newZoom !== this.mainCam.zoom) {
        // Get current camera center in world coordinates
        // const worldPoint = this.mainCam.getWorldPoint(pointer.x, pointer.y);
        
        // Set new zoom
        this.mainCam.zoom = newZoom;
      }
    });
    
    // Set up component event listeners
    this.events.on('autoAssemble', this.handleAutoAssemble, this);
    this.events.on('reset', this.handleReset, this);
    this.events.on('continue', this.handleContinue, this);
    
    // Setup debug system
    this.setupDebugKeyListener();
    
    // Add back button
    this.createBackButton();
    
    // Set camera masks to ensure proper rendering
    // Main camera should ignore UI elements
    this.mainCam.ignore(this.uiContainer);
    
    // UI camera should only see UI elements and ignore game elements
    this.uiCam.ignore([this.gradientBackground, ...this.components, this.protoCell]);
    
    // Make tooltip visible at a high depth
    this.tooltip.setDepth(9999);
    
    // Handle window resize
    this.scale.on('resize', this.handleResize, this);
    
    // Initial resize handling
    this.handleResize(this.scale.gameSize);
    
    // Center camera on protocell
    this.centerCameraOnProtoCell();
    
    // Log draggable components
    const draggableLipids = this.components.filter(c => 
      c.getData('type') === 'lipid' && c.input && c.input.draggable).length;
    const draggableMolecules = this.components.filter(c => 
      c.getData('type') === 'molecule' && c.input && c.input.draggable).length;
    
    console.log(`Draggable components: ${draggableLipids} lipids, ${draggableMolecules} molecules`);
  }

  update(time: number, delta: number): void {
    // Update systems
    this.bubbleSystem.update();
    this.uiSystem.update();
    
    // Execute auto-assembly if active
    if (this.autoAssembleMode) {
      this.executeAutoAssembly();
    }
    
    // Update membrane stability based on assembled components
    if (this.protoCell && this.protoCell.active) {
      this.membraneStability = this.protoCell.progress;
    }
    
    // Update cell
    if (this.cell) {
      this.cell.update(delta);
    }
    
    // Update living membrane if it exists
    if (this.livingMembrane && this.livingMembrane.active) {
      this.livingMembrane.update(delta);
      
      // Ensure proper camera relationships for the living membrane
      if (!this.protoCell || !this.protoCell.active) {
        // Make sure membrane has correct depth
        this.livingMembrane.setDepth(200);
        
        // Ensure membrane is rendered by main camera
        if (this.mainCam && this.mainCam.renderList && 
            !this.mainCam.renderList.includes(this.livingMembrane)) {
          this.mainCam.renderList.push(this.livingMembrane);
        }
        
        // Make sure UI camera ignores the membrane
        if (this.uiCam) {
          this.uiCam.ignore(this.livingMembrane);
        }
        
        // Position gradient properly
        if (this.gradientBackground && this.livingMembrane) {
          this.gradientBackground.setPosition(this.livingMembrane.x, this.livingMembrane.y);
        }
      }
    }
    
    // Update membrane stability meter directly via GameUISystem
    if (this.protoCell && this.livingMembrane) {
      const stability = this.protoCell.progress;
      this.gameUI.updateProgressBar(
        'stability', 
        stability, 
        `Membrane Stability: ${Math.round(stability)}%`
      );
    }
    
    // Update debug info if visible
    if (this.isDebugVisible) {
      this.updateDebugInfo();
    }
    
    // Update FPS display if enabled
    if (this.fpsText) {
      this.fpsText.setText(`FPS: ${Math.round(this.game.loop.actualFps)}`);
    }
  }
  
  private setupDragEvents(): void {
    console.log('Setting up minimal drag events for ProtocellScene');
    
    try {
      // Handle object dragging
      this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: any, dragX: number, dragY: number) => {
        if (!gameObject || !gameObject.active) return;
        if (gameObject.isCorrectlyPositioned && gameObject.isCorrectlyPositioned()) return;
        
        // Update position
        gameObject.x = dragX;
        gameObject.y = dragY;
        
        // Hide tooltip while dragging
        this.tooltip.hide();
      });
      
      // Handle drag start 
      this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: any) => {
        if (!gameObject || !gameObject.active) return;
        
        console.log('Drag started on:', gameObject.getData('type'));
        gameObject.setDepth(100);
        this.tweens.killTweensOf(gameObject);
        
        // Hide tooltip when starting to drag
        this.tooltip.hide();
      });
      
      // Handle drag end
      this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: any) => {
        if (!gameObject || !gameObject.active) return;
        
        console.log('Drag ended on:', gameObject.getData('type'));
        gameObject.setDepth(10);
        
        // Check component position
        const positionResult = this.protoCell.checkComponentPosition(gameObject);
        
        // Reapply floating if not positioned
        if (!positionResult && gameObject.applyFloatingEffect) {
          gameObject.applyFloatingEffect();
        }
      });
      
      // Handle pointer over for tooltips
      this.input.on('pointerover', (pointer: Phaser.Input.Pointer, gameObjects: Phaser.GameObjects.GameObject[]) => {
        if (!gameObjects || gameObjects.length === 0) return;
        
        const gameObject = gameObjects[0];
        const tooltipText = gameObject.getData('tooltip');
        
        if (tooltipText) {
          // Show tooltip at pointer position
          this.tooltip.show(pointer.x, pointer.y, tooltipText);
          console.log('Showing tooltip for:', gameObject.getData('type'), tooltipText);
        }
      });
      
      // Handle pointer out for tooltips
      this.input.on('pointerout', (pointer: Phaser.Input.Pointer, gameObjects: Phaser.GameObjects.GameObject[]) => {
        if (!gameObjects || gameObjects.length === 0) return;
        
        // Hide tooltip when pointer leaves object
        this.tooltip.hide();
      });
      
      console.log('Drag event setup complete');
    } catch (error) {
      console.error('Error setting up drag events:', error);
    }
  }
  
  private createInitialComponents(): void {
    // Create lipids
    for (let i = 0; i < 30; i++) {
      this.createLipid();
    }
    
    // Create molecules of different types
    const moleculeTypes: MoleculeType[] = ['simple', 'rna', 'protein', 'enzyme'];
    for (let i = 0; i < 15; i++) {
      const typeIndex = i % moleculeTypes.length;
      this.createMolecule(moleculeTypes[typeIndex]);
    }
    
    // Debug: log interaction status
    console.log('Input system enabled:', this.input.enabled);
    console.log('Input system active:', this.input.isActive());
    
    // Count draggable components
    const draggableLipids = this.components.filter(c => 
      c.getData('type') === 'lipid' && c.input && c.input.draggable).length;
    const draggableMolecules = this.components.filter(c => 
      c.getData('type') === 'molecule' && c.input && c.input.draggable).length;
      
    console.log(`Draggable components: ${draggableLipids} lipids, ${draggableMolecules} molecules`);
  }
  
  private createLipid(): Lipid {
    // Random position outside the cell
    const angle = Math.random() * Math.PI * 2;
    const distanceFromCenter = 350 + Math.random() * 100;
    const x = this.scale.width / 2 + Math.cos(angle) * distanceFromCenter;
    const y = this.scale.height / 2 + Math.sin(angle) * distanceFromCenter;
    
    // Random color
    const colorIndex = Math.floor(Math.random() * this.lipidColors.length);
    const color = this.lipidColors[colorIndex];
    
    // Create lipid
    const lipid = new Lipid(this, x, y, color);
    lipid.setRotation(Math.random() * Math.PI * 2);
    
    // Re-enable floating effect
    lipid.applyFloatingEffect();
    
    // Add to components array
    this.components.push(lipid);
    
    return lipid;
  }
  
  private createMolecule(type: MoleculeType = 'simple'): Molecule {
    // Random position outside the cell
    const angle = Math.random() * Math.PI * 2;
    const distanceFromCenter = 350 + Math.random() * 100;
    const x = this.scale.width / 2 + Math.cos(angle) * distanceFromCenter;
    const y = this.scale.height / 2 + Math.sin(angle) * distanceFromCenter;
    
    // Random color
    const colorIndex = Math.floor(Math.random() * this.moleculeColors.length);
    const color = this.moleculeColors[colorIndex];
    
    // Create molecule
    const molecule = new Molecule(this, x, y, color, type);
    
    // Re-enable floating effect
    molecule.applyFloatingEffect();
    
    // Add to components array
    this.components.push(molecule);
    
    return molecule;
  }
  
  private handleAutoAssemble(active: boolean): void {
    console.log('Auto-assembly triggered, active:', active);
    
    // Store auto-assembly mode state
    this.autoAssembleMode = active;
    
    if (active) {
      // When auto-assembly is triggered, find and auto-place any unpositioned components
      console.log('Auto-assembly activated - slots will now be assigned to all lipids');
      
      // Execute auto-assembly immediately once
      this.executeAutoAssembly();
    } else {
      console.log('Auto-assembly deactivated');
    }
  }
  
  private executeAutoAssembly(): void {
    // Execute auto assembly at intervals
    // This is called from update when autoAssembleMode is true
    if (this.autoAssembleMode && !this.protoCell.isComplete) {
      console.log('Executing auto-assembly with', this.components.length, 'components');
      
      // Make sure we have the latest list of components
      const unpositionedComponents = this.components.filter(c => !c.isCorrectlyPositioned());
      
      // Count lipids and molecules
      const unpositionedLipids = unpositionedComponents.filter(c => c.getData('type') === 'lipid').length;
      const unpositionedMolecules = unpositionedComponents.filter(c => c.getData('type') === 'molecule').length;
      
      console.log(`Found ${unpositionedComponents.length} unpositioned components: ${unpositionedLipids} lipids, ${unpositionedMolecules} molecules`);
      
      // Call auto-assembly
      this.protoCell.autoAssemble(unpositionedComponents);
      
      // Don't turn off auto-assemble mode until complete
      if (this.protoCell.isComplete) {
        console.log('Auto-assembly complete!');
        this.autoAssembleMode = false;
        this.createCompletionPulseEffect();
      }
    }
  }
  
  private handleReset(): void {
    // Reset all components positions
    this.components.forEach(component => {
      const angle = Math.random() * Math.PI * 2;
      const distanceFromCenter = 350 + Math.random() * 100;
      const x = this.scale.width / 2 + Math.cos(angle) * distanceFromCenter;
      const y = this.scale.height / 2 + Math.sin(angle) * distanceFromCenter;
      
      component.setPositioned(false);
      
      // Animate to new position
      this.tweens.add({
        targets: component,
        x: x,
        y: y,
        rotation: component.getData('type') === 'lipid' ? Math.random() * Math.PI * 2 : undefined,
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          // Re-apply floating effect
          if (component.applyFloatingEffect) {
            component.applyFloatingEffect();
          }
        }
      });
    });
    
    // Reset protocell
    this.protoCell.reset();
    
    // Reset auto-assemble mode
    this.autoAssembleMode = false;
  }
  
  private handleContinue(): void {
    // Prepare data to pass to the next scene
    const evolutionData = {
      membraneStability: this.membraneStability,
      absorbedLipids: this.absorbedLipids,
      absorbedProteins: this.absorbedProteins,
    };
    
    // Perform cleanup
    this.performCleanup();
    
    // Transition to next scene
    this.scene.stop('ProtocellScene');
    this.scene.start('CytoskeletonScene', evolutionData);
  }

  private createBackButton(): void {
    // Add back button
    this.backButton = this.add.text(this.scale.width - 20, 20, 'Menu', {
      fontSize: '18px',
      color: '#ffffff',
      backgroundColor: '#1a6dd7',
      padding: { x: 10, y: 5 },
      stroke: '#000000',
      strokeThickness: 1
    })
    .setOrigin(1, 0)
    .setInteractive({ useHandCursor: true })
    .on('pointerdown', () => {
      console.log('Back button clicked, returning to MainMenuScene');
      this.performCleanup();
      this.scene.stop('ProtocellScene');
      this.scene.start('MainMenuScene');
    });
    
    // Add to UI container to ensure it's visible in UI camera
    this.uiContainer.add(this.backButton);
  }

  // Create a radial gradient background that's brightest at the center
  private createGradientBackground(): void {
    console.log('--- Creating protocell stage gradient background ---');
    
    // First, completely clean up any existing background elements
    if (this.gradientBackground) {
      console.log('Destroying existing gradient reference');
      this.gradientBackground.destroy();
      this.gradientBackground = null as any;
    }
    
    // Remove any existing texture with this name - use a unique name for this scene
    const textureName = 'protocellGradientTexture';
    if (this.textures.exists(textureName)) {
      console.log('Removing existing gradient texture');
      this.textures.remove(textureName);
    }
    
    // Create a temporary graphics object for the gradient
    const tempGraphics = this.add.graphics();
    tempGraphics.name = 'tempGradientGraphics';
    tempGraphics.clear();
    
    // Make the gradient large enough for this stage
    const size = 6000;
    const center = size / 2;
    
    // Define colors for primordial ocean environment
    const colorStops = [
      { color: 0x000022, radius: 1.0 },     // Deep ocean (outer edge)
      { color: 0x000044, radius: 0.85 },    // Dark blue
      { color: 0x000066, radius: 0.70 },    // Medium blue
      { color: 0x000088, radius: 0.55 },    // Brighter blue
      { color: 0x0000aa, radius: 0.40 },    // Vibrant blue
      { color: 0x0000cc, radius: 0.25 },    // Light blue
      { color: 0x2222ff, radius: 0.10 },    // Bright blue
      { color: 0x4444ff, radius: 0.05 }     // Center highlight
    ];
    
    // Draw the concentric circles from outside in
    for (let i = 0; i < colorStops.length; i++) {
      const { color, radius } = colorStops[i];
      tempGraphics.fillStyle(color, 1); // Solid with no transparency
      tempGraphics.fillCircle(center, center, center * radius);
    }
    
    // Generate a texture with a unique name for this scene
    tempGraphics.generateTexture(textureName, size, size);
    
    // Clean up the temporary graphics object
    tempGraphics.clear();
    tempGraphics.destroy();
    
    // Create a sprite at exactly world origin (0,0)
    const gradientSprite = this.add.sprite(0, 0, textureName);
    gradientSprite.setOrigin(0.5);
    
    // Ensure this is drawn behind everything with a deep negative depth
    gradientSprite.setDepth(-10000);
    gradientSprite.name = 'protocellGradientBackground';
    
    // Make sure the background is NOT interactive in any way
    gradientSprite.input = null;
    
    // Store reference to sprite
    this.gradientBackground = gradientSprite;
  }

  // Handle window resize
  private handleResize(gameSize: Phaser.Structs.Size): void {
    // Update UI camera size
    if (this.uiCam) {
      this.uiCam.setSize(gameSize.width, gameSize.height);
    }
    
    // Update UI positions based on new game size
    if (this.backButton) {
      this.backButton.setPosition(gameSize.width - 20, 20);
    }
    
    // Update UI system
    if (this.uiSystem) {
      this.uiSystem.update();
    }
    
    // Handle camera positioning based on what's active in the scene
    if (this.protoCell && this.protoCell.active) {
      // If protocell exists and is active, center on it
      this.centerCameraOnProtoCell();
      
      // Update gradient background position to match
      if (this.gradientBackground) {
        this.gradientBackground.setPosition(this.protoCell.x, this.protoCell.y);
      }
    } else if (this.livingMembrane && this.livingMembrane.active) {
      // If we have a living membrane but no protocell, center on the membrane
      // This prevents the duplicate membrane issue when resizing
      const membraneX = this.livingMembrane.x;
      const membraneY = this.livingMembrane.y;
      
      // Center the main camera on the membrane
      if (this.mainCam) {
        this.mainCam.centerOn(membraneX, membraneY);
      }
      
      // Update gradient background position to match
      if (this.gradientBackground) {
        this.gradientBackground.setPosition(membraneX, membraneY);
      }
    }
    
    // Update UI positions with built-in resize handling
    if (this.gameUI) {
      this.gameUI.handleResize();
    }
  }

  // Helper method to center the camera on the protocell
  private centerCameraOnProtoCell(): void {
    if (this.protoCell && this.mainCam) {
      // Get the protocell's position
      const cellX = this.protoCell.x;
      const cellY = this.protoCell.y;
      
      // Center the main camera on the protocell
      this.mainCam.centerOn(cellX, cellY);
    }
  }

  // Helper method to create a pulse effect when protocell assembly is complete
  private createCompletionPulseEffect(): void {
    if (!this.protoCell) return;
    
    // Get the protocell position
    const cellX = this.protoCell.x;
    const cellY = this.protoCell.y;
    
    // Create a single, elegant pulse wave that expands outward
    const pulse = this.add.circle(cellX, cellY, 10, 0xaaddff, 0.7);
    pulse.setDepth(500); // Above the protocell but below UI
    
    // Add a tween to expand the circle with a larger radius
    this.tweens.add({
      targets: pulse,
      radius: 500, // Much larger radius for a more dramatic wave
      alpha: 0,
      duration: 1500,
      ease: 'Sine.easeOut',
      onComplete: () => {
        // Remove the pulse object when animation is complete
        pulse.destroy();
      }
    });
    
    // Briefly expand the protocell membrane to emphasize transformation
    // First, store the original scale
    const originalScaleX = this.protoCell.scaleX;
    const originalScaleY = this.protoCell.scaleY;
    
    // Create expansion effect
    this.tweens.add({
      targets: this.protoCell,
      scaleX: originalScaleX * 1.15, // Expand by 15%
      scaleY: originalScaleY * 1.15, // Expand by 15%
      duration: 400,
      ease: 'Sine.easeOut',
      onComplete: () => {
        // Return to original size with a slight bounce
        this.tweens.add({
          targets: this.protoCell,
          scaleX: originalScaleX,
          scaleY: originalScaleY,
          duration: 500,
          ease: 'Back.easeOut',
        });
      }
    });
    
    // Add a subtle glow effect to the protocell
    const glow = this.add.circle(cellX, cellY, 60, 0xffffff, 0.3);
    glow.setDepth(499); // Just below the protocell
    
    this.tweens.add({
      targets: glow,
      alpha: 0.6,
      radius: 70,
      yoyo: true,
      duration: 800,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        glow.destroy();
      }
    });
    
    // Add a subtle camera shake effect
    this.cameras.main.shake(200, 0.002);
    
    console.log('Protocell completion pulse effect created');
  }

  // Helper method for cleanup - enhanced for thorough cleanup
  private performCleanup(): void {
    console.log('ProtocellScene performing thorough cleanup');
    
    // Stop systems
    this.bubbleSystem.stop();
    
    // Remove all event listeners FIRST before destroying objects
    // Remove input system listeners
    this.input.off('drag');
    this.input.off('dragstart');
    this.input.off('dragend');
    this.input.off('gameobjectover');
    this.input.off('gameobjectout');
    this.input.off('pointermove');
    this.input.off('pointerdown');
    this.input.off('pointerup');
    this.input.off('wheel'); // Remove wheel event listener
    
    // Remove resize listener
    this.scale.off('resize', this.handleResize, this);
    
    // Clean up UI system
    if (this.uiSystem) {
        this.scale.off('resize', this.uiSystem.handleResize, this.uiSystem);
    }
    
    // Disable input system
    this.input.enabled = false;
    
    // Explicitly clear all input from components before destroying them
    this.components.forEach(component => {
      if (component && component.input) {
        // Remove drag ability
        component.input.draggable = false;
        
        // Disable interactive
        component.disableInteractive();
      }
    });
    
    // Clean up the living membrane if it exists
    if (this.livingMembrane) {
      try {
        // Cast to any to avoid TypeScript errors during cleanup
        (this.livingMembrane as any).destroy();
      } catch (e) {
        console.error('Error destroying livingMembrane:', e);
      }
      this.livingMembrane = null;
    }
    
    // Kill all tweens
    this.tweens.killAll();
    
    // Remove all components
    this.components.forEach(component => {
      if (component) {
        component.destroy();
      }
    });
    this.components = [];
    
    // Destroy gradient background explicitly
    if (this.gradientBackground) {
      console.log('Destroying gradient background in cleanup');
      this.gradientBackground.destroy();
      this.gradientBackground = null as any;
    }
    
    // Remove any leftover textures - use the unique texture name
    if (this.textures.exists('protocellGradientTexture')) {
      console.log('Removing gradient texture in ProtocellScene cleanup');
      this.textures.remove('protocellGradientTexture');
    }
    
    // Remove scene event listeners
    this.events.off('autoAssemble', this.handleAutoAssemble, this);
    this.events.off('reset', this.handleReset, this);
    this.events.off('continue', this.handleContinue, this);
    
    // Remove protocell event listeners
    if (this.protoCell && this.protoCell.completeEmitter) {
      this.protoCell.completeEmitter.off('complete', this.handleProtocellCompletion, this);
    }

    // Remove the keyboard listener for debug
    if (this.input.keyboard) {
      this.input.keyboard.off('keydown-D');
    }

    // Clean up debug UI if it exists
    if (this.debugContainer) {
      if (this.debugBackground) this.debugBackground.destroy();
      if (this.debugTitle) this.debugTitle.destroy();
      if (this.debugStats) this.debugStats.destroy();
      this.debugContainer.destroy();
      this.debugContainer = null;
      this.debugBackground = null;
      this.debugTitle = null;
      this.debugStats = null;
    }

    // Clean up FPS display if it exists
    if (this.fpsText) {
      this.fpsText.destroy();
      this.fpsText = null;
    }

    console.log('ProtocellScene cleanup complete');
  }

  // Helper method for cleanup
  shutdown(): void {
    console.log('ProtocellScene shutdown method called');
    
    // Perform cleanup
    this.performCleanup();
    
    console.log('ProtocellScene shutdown complete');
  }

  private handleProtocellCompletion(): void {
    // Avoid multiple triggers by checking if we're already processing completion
    if (this.livingMembrane !== null) {
      console.log('Already created living membrane, ignoring additional completion events');
      return;
    }

    console.log('Protocell completion event received - first time');
    
    // Create the pulse effect
    this.createCompletionPulseEffect();
    
    // Cleanup any existing membrane to prevent duplicates
    if (this.livingMembrane) {
      try {
        // Cast to any to avoid TypeScript errors during cleanup
        (this.livingMembrane as any).destroy();
      } catch (e) {
        console.error('Error destroying livingMembrane:', e);
      }
      this.livingMembrane = null;
    }
    
    // Immediately create an empty membrane object to prevent additional calls from creating membranes
    this.livingMembrane = new Membrane(
      this,
      this.protoCell.x,
      this.protoCell.y,
      this.protoCell.getRadius(),
      {
        // Configuration for early cell membrane (simple, like LivingMembrane)
        showBilayer: false,
        wobbleAmplitude: [0.04, 0.02, 0.01],
        wobbleFrequency: [2, 5, 8],
        wobbleSpeed: 0.004,
        showPhospholipids: false,
        allowBreaches: true, // Enable breaches
        respondToMovement: true,
        outerColor: 0x88ccff,
        fillColor: 0xaaddff,
        outerAlpha: 0.8,
        fillAlpha: 0.4,
        outerLineWidth: 3
      }
    );
    
    // Set proper depth to ensure it's rendered in the right order
    this.livingMembrane.setDepth(200);
    
    // Clear previous camera settings
    if (this.mainCam && this.mainCam.renderList) {
      // Remove any duplicated membrane instances
      this.mainCam.renderList = this.mainCam.renderList.filter(obj => obj !== this.livingMembrane);
      // Add to renderList once
      this.mainCam.renderList.push(this.livingMembrane);
    }
    
    // Make sure UI camera ignores the living membrane
    if (this.uiCam) {
      this.uiCam.ignore(this.livingMembrane);
    }
    
    // Make sure the gradient background follows the membrane
    if (this.gradientBackground && this.livingMembrane) {
      this.gradientBackground.setPosition(this.livingMembrane.x, this.livingMembrane.y);
    }
    
    // Make it invisible while we wait for the transition
    this.livingMembrane.setAlpha(0);

    // Remove the completion listener to ensure it can't fire again
    this.protoCell.completeEmitter.off('complete', this.handleProtocellCompletion, this);

    // Hide and remove all components when transitioning to living membrane
    this.components.forEach(component => {
      // Store the lipid count before removing correctly positioned components
      if (component.getData('type') === 'lipid') {
        if (component.isCorrectlyPositioned()) {
          this.absorbedLipids++; // Increment the count of positioned lipids
          
          // Fade out only correctly positioned lipids
          this.tweens.add({
            targets: component,
            alpha: 0,
            duration: 800,
            ease: 'Power2',
            onComplete: () => {
              component.destroy();
            }
          });
        }
        // Leave floating lipids alone - they stay visible
      } else if (component.getData('type') === 'molecule') {
        if (component.isCorrectlyPositioned()) {
          this.absorbedProteins++; // Increment the count of positioned molecules
        }
        
        // Fade out all molecules regardless of position
        this.tweens.add({
          targets: component,
          alpha: 0,
          duration: 800,
          ease: 'Power2',
          onComplete: () => {
            component.destroy();
          }
        });
      }
    });
    
    // Delay showing the success message to allow more time to appreciate the pulse effect
    this.time.delayedCall(3000, () => {
      console.log('Transitioning to living membrane');
      
      // Fade out the protocell
      this.tweens.add({
        targets: this.protoCell,
        alpha: 0,
        duration: 1000,
        ease: 'Power2',
        onComplete: () => {
          // Remove the protocell from the scene
          if (this.protoCell) {
            this.protoCell.destroy();
            
            // Update camera to focus on living membrane after protocell is gone
            if (this.livingMembrane && this.livingMembrane.active) {
              this.mainCam.centerOn(this.livingMembrane.x, this.livingMembrane.y);
              
              if (this.gradientBackground) {
                this.gradientBackground.setPosition(this.livingMembrane.x, this.livingMembrane.y);
              }
            }
          }
        }
      });
      
      // Fade in the living membrane
      this.tweens.add({
        targets: this.livingMembrane,
        alpha: 1,
        duration: 1000,
        ease: 'Power2'
      });
      
      // Set up right-click for breach after living membrane is visible
      this.setupBreachInteraction();
    });
  }

  private setupBreachInteraction(): void {
    console.log('Setting up breach interaction for living membrane');
    
    // Create a hit area for the membrane to detect clicks
    if (this.livingMembrane) {
      // Make sure the living membrane has the correct depth and is in proper camera lists
      this.livingMembrane.setDepth(200);
      
      // Ensure membrane is visible in main cam but not in UI cam
      this.uiCam.ignore(this.livingMembrane);
      
      // Make sure gradient background is positioned correctly
      if (this.gradientBackground) {
        this.gradientBackground.setPosition(this.livingMembrane.x, this.livingMembrane.y);
      }
      
      // Make the membrane interactive
      this.livingMembrane.setInteractive(
        new Phaser.Geom.Circle(0, 0, this.livingMembrane.baseRadius || 100),
        Phaser.Geom.Circle.Contains
      );
      
      // Listen for right-clicks on the membrane
      this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        // Only handle right clicks
        if (pointer.rightButtonDown() && this.livingMembrane) {
          // Get membrane position
          const membraneX = this.livingMembrane.x;
          const membraneY = this.livingMembrane.y;
          
          // Calculate if the click is within the membrane radius
          const dx = pointer.worldX - membraneX;
          const dy = pointer.worldY - membraneY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // If click is on the membrane
          const radius = this.livingMembrane.baseRadius || 100;
          if (distance <= radius + 10) { // Add a small buffer for easier clicking
            // Calculate angle for breach
            const angle = Math.atan2(dy, dx);
            
            console.log('Creating breach at angle:', angle);
            
            // Create breach
            this.livingMembrane.createBreach(angle, 0.3);
            
            // Add a visual effect to make it clear a breach has occurred
            this.createBreachEffect(membraneX + dx, membraneY + dy);
          }
        }
      }, this);
    }
  }

  private createBreachEffect(x: number, y: number): void {
    // Check if the particle texture exists
    if (!this.textures.exists('particle')) {
      // Create a simple particle texture if it doesn't exist
      const graphics = this.add.graphics();
      graphics.fillStyle(0xffffff);
      graphics.fillCircle(4, 4, 4);
      graphics.generateTexture('particle', 8, 8);
      graphics.destroy();
    }
    
    // Create a red burst effect at the breach location
    const particles = this.add.particles(x, y, 'particle', {
      speed: { min: 50, max: 100 },
      scale: { start: 0.2, end: 0 },
      lifespan: 800,
      quantity: 15,
      alpha: { start: 0.8, end: 0 },
      tint: [0xff0000],
      emitting: false
    });
    
    // Emit once then destroy
    particles.explode(15);
    this.time.delayedCall(800, () => {
      particles.destroy();
    });
    
    // Flash the membrane red briefly
    if (this.livingMembrane) {
      this.livingMembrane.setTint(0xff0000);
      this.time.delayedCall(200, () => {
        this.livingMembrane?.clearTint();
      });
    }
  }

  // Create and setup debug window
  private createDebugWindow(): void {
    // Create container for debug window
    this.debugContainer = this.add.container(this.scale.width - 20, 100);
    this.debugContainer.setDepth(10000); // Above everything
    
    // Create background rectangle
    this.debugBackground = this.add.rectangle(0, 0, 300, 350, 0x000000, 0.8);
    this.debugBackground.setOrigin(1, 0); // Align to right
    this.debugContainer.add(this.debugBackground);
    
    // Create title
    this.debugTitle = this.add.text(-290, 10, "DEBUG PANEL", {
      fontSize: '18px',
      fontFamily: 'Arial',
      color: '#FFFFFF',
      fontStyle: 'bold'
    });
    this.debugContainer.add(this.debugTitle);
    
    // Create stats text
    this.debugStats = this.add.text(-290, 40, "Loading stats...", {
      fontSize: '14px',
      fontFamily: 'monospace',
      color: '#CCCCCC',
      align: 'left',
      wordWrap: { width: 280 }
    });
    this.debugContainer.add(this.debugStats);
    
    // Initial state - hidden
    this.debugContainer.setVisible(false);
    this.isDebugVisible = false;
    
    // Make sure UI camera renders this
    if (this.uiCam) {
      // Remove from main camera's render list if it's there
      if (this.mainCam && this.mainCam.renderList) {
        this.mainCam.renderList = this.mainCam.renderList.filter(obj => obj !== this.debugContainer);
      }
    }
    
    // Add to UI container
    this.uiContainer.add(this.debugContainer);
    
    console.log("Debug window created");
  }

  // Update debug information
  private updateDebugInfo(): void {
    if (!this.debugStats || !this.isDebugVisible) return;
    
    let debugText = "";
    
    // Basic scene info
    debugText += `FPS: ${Math.round(this.game.loop.actualFps)}\n`;
    debugText += `Camera Zoom: ${this.mainCam.zoom.toFixed(2)}\n`;
    debugText += `Components: ${this.components.length}\n`;
    debugText += "---------------------------\n";
    
    // Cell stats
    debugText += "CELL STATS:\n";
    debugText += `Membrane Stability: ${(this.membraneStability * 100).toFixed(1)}%\n`;
    debugText += `Absorbed Lipids: ${this.absorbedLipids}\n`;
    debugText += `Absorbed Proteins: ${this.absorbedProteins}\n`;
    debugText += "---------------------------\n";
    
    // Membrane info (if exists)
    if (this.livingMembrane) {
      debugText += "MEMBRANE INFO:\n";
      debugText += `Position: (${Math.round(this.livingMembrane.x)}, ${Math.round(this.livingMembrane.y)})\n`;
      debugText += `Radius: ${this.livingMembrane.baseRadius}\n`;
      debugText += `Active: ${this.livingMembrane.active}\n`;
      // Using the new getBreachCount method
      debugText += `Breaches: ${this.livingMembrane.getBreachCount()}\n`;
      debugText += `Visible: ${this.livingMembrane.alpha > 0 ? 'Yes' : 'No'}\n`;
    } else if (this.protoCell) {
      debugText += "PROTOCELL INFO:\n";
      debugText += `Position: (${Math.round(this.protoCell.x)}, ${Math.round(this.protoCell.y)})\n`;
      debugText += `Progress: ${(this.protoCell.progress * 100).toFixed(1)}%\n`;
      debugText += `Complete: ${this.protoCell.isComplete ? 'Yes' : 'No'}\n`;
    }
    
    // Update the text
    this.debugStats.setText(debugText);
  }

  // Add key listener for debug panel toggle
  private setupDebugKeyListener(): void {
    // Add key combination for debug panel
    this.input.keyboard?.on('keydown-D', (event: KeyboardEvent) => {
      // Toggle debug panel on Ctrl+D
      if (event.ctrlKey) {
        this.toggleDebugPanel();
        event.preventDefault();
      }
    });
    
    console.log("Debug key listener setup");
  }

  // Toggle debug panel visibility
  private toggleDebugPanel(): void {
    if (!this.debugContainer) {
      this.createDebugWindow();
    }
    
    this.isDebugVisible = !this.isDebugVisible;
    
    if (this.debugContainer) {
      this.debugContainer.setVisible(this.isDebugVisible);
      
      // Update info immediately if showing
      if (this.isDebugVisible) {
        this.updateDebugInfo();
      }
    }
    
    console.log(`Debug panel toggled: ${this.isDebugVisible ? 'visible' : 'hidden'}`);
  }

  // Create FPS display if enabled in settings
  private createFpsDisplay(): void {
    if (this.settingsManager && this.settingsManager.isFpsDisplayEnabled()) {
      this.fpsText = this.add.text(10, 10, 'FPS: 0', {
        fontSize: '18px',
        color: '#ffffff',
        backgroundColor: '#000000',
        padding: { x: 5, y: 2 },
        stroke: '#ffffff',
        strokeThickness: 2
      });
      this.fpsText.setDepth(10000); // Above everything
    }
  }
} 